import { memo, useMemo } from "react";
import { Link } from "react-router";

import type { RoomList_DesignPageQuery } from "@/lib/gql/__generated__/graphql";

import { BlankContentForList } from "@/components/features/_components";
import { FeatureIslandContainer, FeatureIslandMainWithBackGround } from "@/components/features/_layouts";

import OpenInNew from "@/components/ui/icon/open-in-new";

import { DesignMagazineHeaderAndTab } from "../../../_shared/ui/components/header-and-tab";
import { DesignMagazinePlanListItem } from "../../../_shared/ui/components/list/list-item";
import { useInitialized } from "../../model/use-case";

type Props = {
	serverModel: RoomList_DesignPageQuery;
	hotelID: string;
};

const ROOM_MAGAZINE_WHERE_TO_DISPLAY_DESCRIPTION_LINK = "https://chillnn.notion.site/f9c0c50b5e874253bb8f403698f9f501";

export const DesignMagazineRoomListPresenter = memo(({ hotelID, serverModel }: Props) => {
	const { roomList } = useInitialized({ serverModel }).data;

	/**
	 * 売り出し中の通常部屋リスト
	 */
	const inSaleOrdinaryRoomList = useMemo(() => roomList.rooms.filter((room) => room.inSale), [roomList.rooms]);
	/**
	 * 売出し中の通常部屋リストを表示できるか
	 */
	const canShowInSaleOrdinaryRoomList = useMemo(
		() => inSaleOrdinaryRoomList.length > 0,
		[inSaleOrdinaryRoomList.length],
	);

	/**
	 * 売り出し中のダミー部屋リスト
	 */
	const inSaleDummyRoomList = useMemo(() => roomList.dummy.filter((room) => room.inSale), [roomList.dummy]);
	/**
	 * 売出し中のダミー部屋リストを表示できるか
	 */
	const canShowInSaleDummyRoomList = useMemo(() => inSaleDummyRoomList.length > 0, [inSaleDummyRoomList]);

	/**
	 * 売り止め中の部屋リスト
	 */
	const inSaleStopRoomList = useMemo(
		() => [...roomList.rooms, ...roomList.dummy].filter((room) => !room.inSale),
		[roomList.rooms, roomList.dummy],
	);
	/**
	 * 売り止め中の部屋リストを表示できるか
	 */
	const canShowInSaleStopRoomList = useMemo(() => inSaleStopRoomList.length > 0, [inSaleStopRoomList]);

	return (
		<FeatureIslandContainer>
			<DesignMagazineHeaderAndTab hotelID={hotelID} />

			<FeatureIslandMainWithBackGround islandTitle="売り出し中の通常部屋">
				<div className="flex flex-wrap gap-x-1 gap-y-2">
					<p>マガジン版 「お部屋」の欄に表示される記事を編集できます。</p>

					<div className="flex items-center gap-x-0.5">
						<p>（</p>
						<Link
							to={ROOM_MAGAZINE_WHERE_TO_DISPLAY_DESCRIPTION_LINK}
							target="_blank"
							className="flex items-center gap-x-1 text-link-400 underline-offset-4 hover:underline"
						>
							表示場所に関する詳細はこちら
							<OpenInNew size="sm" />
						</Link>
						<p>）</p>
					</div>
				</div>

				{canShowInSaleOrdinaryRoomList ? (
					<>
						{inSaleOrdinaryRoomList.map((item) => {
							return (
								<DesignMagazinePlanListItem
									key={item.id}
									searchParams={{ resourceID: item.roomID, resourceType: "Room" }}
									hotelID={hotelID}
									id={item.id}
									mainKeyVisual={item.mainKeyVisual}
									name={item.name}
								/>
							);
						})}
					</>
				) : (
					<BlankContentForList content="売り出し中の通常部屋の記事はありません。" />
				)}
			</FeatureIslandMainWithBackGround>

			<FeatureIslandMainWithBackGround islandTitle="売り出し中のダミー部屋">
				{canShowInSaleDummyRoomList ? (
					<>
						{inSaleDummyRoomList.map((item) => {
							return (
								<DesignMagazinePlanListItem
									key={item.id}
									searchParams={{ resourceID: item.roomID, resourceType: "Room" }}
									hotelID={hotelID}
									id={item.id}
									mainKeyVisual={item.mainKeyVisual}
									name={item.name}
								/>
							);
						})}
					</>
				) : (
					<BlankContentForList content="売り出し中のダミー部屋の記事はありません。" />
				)}
			</FeatureIslandMainWithBackGround>

			<FeatureIslandMainWithBackGround islandTitle="売り止め中の部屋">
				{canShowInSaleStopRoomList ? (
					<>
						{inSaleStopRoomList.map((item) => {
							return (
								<DesignMagazinePlanListItem
									key={item.id}
									searchParams={{ resourceID: item.roomID, resourceType: "Room" }}
									hotelID={hotelID}
									id={item.id}
									mainKeyVisual={item.mainKeyVisual}
									name={item.name}
								/>
							);
						})}
					</>
				) : (
					<BlankContentForList content="売り止め中の部屋の記事はありません。" />
				)}
			</FeatureIslandMainWithBackGround>
		</FeatureIslandContainer>
	);
});

DesignMagazineRoomListPresenter.displayName = "DesignMagazineRoomList";
